import { useCallback, useMemo, useState } from 'react';
import { Chip, Stack, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import type { GridColDef } from '@mui/x-data-grid';
import { usePageTitle } from '../../components/shell/AppShell';
import { RouteDataGrid } from '../../components/data-grid/RouteDataGrid';
import type { QuickFilter, RowAction } from '../../components/data-grid/RouteDataGrid';
import { fmtDateTime } from '../../utils/format';
import type { ExportColumn } from '../../utils/export';
import type { MachineQrBatchSummary } from '../../api/types';
import {
  useMyPermissions,
  useMachineQrBatchesList,
} from '../../api/queries';
import { apiBaseUrl, TOKEN_STORAGE_KEY } from '../../auth/config';
import { GenerateMachineQrBatchDialog } from './GenerateMachineQrBatchDialog';
import { useAlert } from '../../components/dialogs/DialogProvider';

/**
 * ESP32 Deployment Plan §3 M6 — machine (cabinet) QR batches. Each batch
 * mints N cabinet QR pairs FOR a specific operator. The field tech scans
 * one of these per machine at D4 install (cabinet sticker) to bind the
 * HAT to the cabinet.
 *
 * Distinct from IoT QR batches (HAT-side stickers, ARMS-stock) and SC QR
 * batches (site-controller stickers).
 */
export default function MachineQrBatchesPage() {
  usePageTitle('Machine (cabinet) QR batches');
  const perms = useMyPermissions();
  const has = useCallback(
    (code: string) => perms.data?.codes?.includes(code) ?? false,
    [perms.data?.codes],
  );

  const navigate = useNavigate();
  const alert = useAlert();
  const { queryKey, listFn } = useMachineQrBatchesList();
  const [generateOpen, setGenerateOpen] = useState(false);

  const columns = useMemo<GridColDef<MachineQrBatchSummary>[]>(() => [
    { field: 'code', headerName: 'Batch', width: 180,
      renderCell: (p) => <code>{p.row.code}</code> },
    // Operator name — never the raw GUID.
    { field: 'intendedOperatorName', headerName: 'Operator', width: 200,
      renderCell: (p) => p.row.intendedOperatorName ?? '—' },
    { field: 'generatedCount', headerName: 'Generated', width: 110,
      renderCell: (p) => p.row.generatedCount.toLocaleString() },
    { field: 'boundCount', headerName: 'Bound', width: 110,
      renderCell: (p) => `${p.row.boundCount.toLocaleString()} / ${p.row.generatedCount.toLocaleString()}` },
    { field: 'status', headerName: 'Status', width: 120,
      renderCell: (p) => <BatchStatusChip status={p.row.status} /> },
    { field: 'notes', headerName: 'Notes', flex: 1,
      renderCell: (p) => p.row.notes
        ? <Typography variant="body2">{p.row.notes}</Typography>
        : <Typography variant="body2" color="text.secondary">—</Typography> },
    { field: 'createdAt', headerName: 'Created', width: 180,
      valueFormatter: (value) => fmtDateTime(value) },
  ], []);

  const quickFilters = useMemo<QuickFilter<MachineQrBatchSummary>[]>(() => [
    { label: 'All',     filter: () => true },
    { label: 'Printed', filter: (b) => b.status === 'printed' },
    { label: 'Voided',  filter: (b) => b.status === 'voided' },
  ], []);

  const exportColumns = useMemo<ExportColumn<MachineQrBatchSummary>[]>(() => [
    { key: 'code',                 label: 'Batch code' },
    { key: 'intendedOperatorName', label: 'Operator' },
    { key: 'requestedCount',       label: 'Requested' },
    { key: 'generatedCount',       label: 'Generated' },
    { key: 'boundCount',           label: 'Bound' },
    { key: 'status',               label: 'Status' },
    { key: 'notes',                label: 'Notes' },
    { key: 'createdAt',            label: 'Created', format: (v) => fmtDateTime(v) },
  ], []);

  const canPrint = has('route.machine-qr-batches.mint');
  const actions = useMemo<RowAction<MachineQrBatchSummary>[]>(() => [
    { label: 'Open',         onClick: (row) => navigate(`/factory/machine-qr-batches/${row.id}`) },
    // Only ARMS callers can print + download CSV (mint permission)
    { label: 'Print',        onClick: (row) => navigate(`/factory/machine-qr-batches/${row.id}/print`),
      visible: () => canPrint },
    { label: 'Download CSV', onClick: (row) => downloadCsv(row.id, row.code, alert),
      visible: () => canPrint },
  ], [navigate, alert, canPrint]);

  if (!has('route.machine-qr-batches.view')) {
    return (
      <Typography color="text.secondary">
        You don&rsquo;t have permission to view machine QR batches.
      </Typography>
    );
  }

  return (
    <Stack spacing={2}>
      <Typography variant="h4">Machine (cabinet) QR batches</Typography>
      <Typography variant="body2" color="text.secondary">
        Cabinet stickers minted per-operator. The field tech scans one at
        machine install (D4) and the bind action wires the cabinet QR,
        the machine row, and the ESP32 HAT together in one step.
      </Typography>

      <RouteDataGrid<MachineQrBatchSummary>
        entityName="Machine QR batches"
        queryKey={queryKey}
        listFn={listFn}
        columns={columns}
        searchFields={[{ kind: 'text', key: 'code', label: 'Batch code' }]}
        quickFilters={quickFilters}
        exportColumns={exportColumns}
        reportTitle="ARMS — Machine (cabinet) QR batches"
        actions={actions}
        primaryAction={
          has('route.machine-qr-batches.mint')
            ? { label: '+ Generate batch', onClick: () => setGenerateOpen(true) }
            : undefined
        }
        onRowClick={(row) => navigate(`/factory/machine-qr-batches/${row.id}`)}
        emptyMessage="No machine QR batches yet."
      />

      <GenerateMachineQrBatchDialog
        open={generateOpen}
        onClose={() => setGenerateOpen(false)}
      />
    </Stack>
  );
}

function BatchStatusChip({ status }: { status: string }) {
  const color =
    status === 'printed' ? 'success' :
    status === 'created' ? 'info' :
    status === 'voided'  ? 'error' :
    'default';
  return <Chip size="small" label={status} color={color as 'success' | 'info' | 'error' | 'default'} />;
}

async function downloadCsv(
  batchId: string, batchCode: string,
  alert: (opts: { title: string; message: string; severity?: 'info' | 'warning' | 'error' | 'success' }) => Promise<void>,
) {
  const url = `${apiBaseUrl}/api/v1/factory/machine-qr-batches/${batchId}/export.csv`;
  const token = sessionStorage.getItem(TOKEN_STORAGE_KEY);
  if (!token) {
    await alert({ title: 'Not signed in', message: 'Sign in and retry the download.', severity: 'warning' });
    return;
  }
  try {
    const r = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
    if (!r.ok) {
      const text = await r.text();
      throw new Error(`HTTP ${r.status}: ${text || r.statusText}`);
    }
    const blob = await r.blob();
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `machine-qr-batch-${batchCode}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(a.href);
  } catch (e) {
    await alert({ title: 'Download failed', message: (e as Error).message, severity: 'error' });
  }
}
